import { useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Eye, EyeOff, Trash2, ExternalLink, Users, UserCheck, ImageIcon, Loader2, Archive } from "lucide-react";
import { api, coachHeaders, TYPES, defautsType, typeLabel, ADRESSE_CLUB, heureAff } from "../lib/api";
import { DEFAULT_MATCH } from "../pages/Convocation";
import { openWhatsApp, buildText, infosLignes, lienReponse } from "./ShareWhatsApp";
import { Ticket } from "./Hero";
import { mapsUrl } from "./MatchDetails";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const tri = (a, b) => `${a.nom} ${a.prenom}`.localeCompare(`${b.nom} ${b.prenom}`, "fr", { sensitivity: "base" });
const btnWa = "inline-flex items-center gap-1 rounded-full bg-[#25D366] px-4 py-2 text-sm font-semibold text-ink transition-transform hover:-translate-y-0.5";

// Bouton « Infos match » : envoie l'image de la carte de convocation (sans la liste des joueurs).
const InfosImage = ({ conv }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const envoyer = async () => {
    setBusy(true);
    try {
      const { toBlob } = await import("html-to-image");
      const blob = await toBlob(ref.current, { pixelRatio: 2, backgroundColor: "#0A1628" });
      const fichier = new File([blob], "infos-convocation.png", { type: "image/png" });
      const maps = mapsUrl(conv.adresse || conv.lieu);
      const texte = [`✅ Réponse (présent / absent) 👉 ${lienReponse(conv)}`, ...(maps ? [`📍 Itinéraire 👉 ${maps}`] : [])].join("\n");
      if (navigator.canShare?.({ files: [fichier] })) {
        await navigator.share({ files: [fichier], text: texte });
        return;
      }
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fichier.name;
      a.click();
      await navigator.clipboard?.write?.([new ClipboardItem({ "image/png": blob })]).catch(() => {});
      toast.success("Image téléchargée et copiée : collez-la (Ctrl+V) dans la conversation WhatsApp", { duration: 6000 });
      openWhatsApp(texte);
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Image impossible à créer");
    } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" data-testid="convocation-wa-infos" onClick={envoyer} disabled={busy} className={`${btnWa} disabled:opacity-60`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}Infos match
      </button>
      <div aria-hidden className="pointer-events-none fixed -left-[9999px] top-0">
        <div ref={ref} data-testid="convocation-infos-image" className="w-[600px] bg-ink p-3"><Ticket match={conv} still /></div>
      </div>
    </>
  );
};

// Boutons admin : partager sur WhatsApp la liste des convoqués et celle des présents.
const ListesWhatsApp = ({ code, conv, reponses }) => {
  const convoques = async () => {
    try {
      const { data } = await api.get("/joueurs", { ...coachHeaders(code), params: { convocation_id: conv.id } });
      const l = (Array.isArray(data) ? data : []).filter((j) => j.convoque);
      if (!l.length) { toast.error("Aucun joueur convoqué pour cette convocation"); return; }
      openWhatsApp(buildText(conv, l));
    } catch { toast.error("Liste indisponible"); }
  };
  const [noteOuverte, setNoteOuverte] = useState(false);
  const cleNote = `note-reponses-${conv.id}`;
  const [note, setNote] = useState(() => localStorage.getItem(cleNote) ?? "");
  const presents = (texteLibre = "") => {
    const r = Array.isArray(reponses) ? reponses : [];
    const p = r.filter((x) => x.statut === "present").sort(tri);
    const a = r.filter((x) => x.statut === "absent").sort(tri);
    if (!r.length) { toast.error("Aucune réponse pour le moment"); return; }
    const nom = (x) => `- ${x.prenom} ${x.nom.toUpperCase()}`;
    const covoit = conv.type !== "entrainement" && (conv.covoit_heure || conv.covoit_lieu)
      ? [`🚗 Covoiturage : ${[conv.covoit_heure && heureAff(conv.covoit_heure), conv.covoit_lieu].filter(Boolean).join(" — ")}`] : [];
    const extra = texteLibre.trim() ? ["", "📝 *Infos* :", texteLibre.trim()] : [];
    openWhatsApp(["Bonjour à tous,", "Voici les réponses à la convocation :", "", ...infosLignes(conv), ...covoit, "", `✅ *Présents (${p.length})* :`, ...p.map(nom), ...(a.length ? ["", `❌ Absents (${a.length}) :`, ...a.map(nom)] : []), ...extra].join("\n"));
  };
  const clicReponses = () => (conv.type === "tournoi" ? setNoteOuverte(true) : presents());
  const envoyerAvecNote = () => { localStorage.setItem(cleNote, note); setNoteOuverte(false); presents(note); };
  return (
    <>
      <InfosImage conv={conv} />
      <button type="button" data-testid="convocation-wa-convoques" onClick={convoques} className={btnWa}><Users className="h-4 w-4" />Convocation</button>
      <button type="button" data-testid="convocation-wa-presents" onClick={clicReponses} className={btnWa}><UserCheck className="h-4 w-4" />Réponses</button>
      <Dialog open={noteOuverte} onOpenChange={setNoteOuverte}>
        <DialogContent data-testid="reponses-note-dialog" className="border-line bg-pitch text-chalk">
          <DialogHeader><DialogTitle className="font-display uppercase">Ajouter un petit texte ?</DialogTitle></DialogHeader>
          <p className="text-sm text-dim">Facultatif : il sera ajouté en bas du message (ex. horaires des matchs du tournoi).</p>
          <textarea data-testid="reponses-note-input" rows={5} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder={"Ex. :\n9h30 : USC - Neufvilles\n10h30 : USC - Soignies\n12h00 : USC - Ath"} className="w-full resize-none rounded-xl border border-line bg-ink px-3 py-2.5 text-base text-chalk outline-none focus:border-lime" />
          <div className="flex flex-wrap justify-end gap-2">
            <button type="button" data-testid="reponses-note-sans" onClick={() => { setNoteOuverte(false); presents(); }} className="rounded-full border border-line px-4 py-2 text-sm text-chalk hover:border-chalk">Envoyer sans texte</button>
            <button type="button" data-testid="reponses-note-envoyer" onClick={envoyerAvecNote} className={btnWa}><UserCheck className="h-4 w-4" />Envoyer</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const ConvocationBar = ({ code, convs, selectedId, onSelect, onChange, admin, reponses }) => {
  const selected = convs.find((c) => c.id === selectedId);
  const [busy, setBusy] = useState(false);
  const creer = async (type) => {
    setBusy(true);
    try {
      const club = convs[0]?.club ?? DEFAULT_MATCH.club;
      const modeles = (await api.get("/modeles", coachHeaders(code)))?.data ?? {};
      const modele = modeles?.[type] ?? {};
      const { data } = await api.post("/convocations", { ...DEFAULT_MATCH, ...defautsType(type), ...modele, lieu: "", competition: typeLabel(type), ...(type === "entrainement" ? { adversaire: "Entraînement", adresse: modele.adresse || modele.lieu || ADRESSE_CLUB } : { adresse: modele.adresse || modele.lieu || "" }), club, type }, coachHeaders(code));
      toast.success(modeles?.[type] ? "Nouvelle convocation pré-remplie avec le modèle" : "Nouvelle convocation créée (aucun modèle enregistré pour ce type)");
      onChange(data?.id);
    } catch { toast.error("Création impossible"); }
    finally { setBusy(false); }
  };
  const afficher = async () => {
    try {
      await api.post(`/convocations/${selectedId}/afficher`, {}, coachHeaders(code));
      toast.success("Convocation affichée aux joueurs");
      onChange(selectedId);
    } catch (e) { toast.error(e?.response?.data?.detail || "Changement impossible"); }
  };
  const supprimer = async () => {
    if (!window.confirm("Supprimer cette convocation et toutes ses réponses (sans résumé) ?")) return;
    await api.delete(`/convocations/${selectedId}`, coachHeaders(code));
    toast.success("Convocation supprimée");
    onChange(null);
  };
  const cloturer = async () => {
    if (!window.confirm("Clôturer cette convocation terminée ?\nUn résumé (présents, absents, pas répondu) sera gardé dans « Convocations passées », puis la convocation et ses réponses seront retirées.")) return;
    try {
      await api.post("/archives", {}, { ...coachHeaders(code), params: { convocation_id: selectedId } });
      toast.success("Convocation clôturée, résumé enregistré");
      onChange(null);
    } catch { toast.error("Clôture impossible"); }
  };

  return (
    <div data-testid="convocation-bar" className="mt-4 rounded-2xl border border-lime/40 bg-pitch p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-display text-2xl uppercase text-chalk">Convocations</h2>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="convocation-new-button" disabled={busy} className="inline-flex items-center gap-1 rounded-full bg-lime px-4 py-2 text-sm font-semibold text-ink disabled:opacity-60"><Plus className="h-4 w-4" />Nouvelle</button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="border-line bg-pitch text-chalk">
            {TYPES.map(([k, l]) => (
              <DropdownMenuItem key={k} data-testid={`convocation-new-${k}`} onSelect={() => creer(k)} className="cursor-pointer focus:bg-lime focus:text-ink">{l}</DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div role="tablist" data-testid="convocation-tabs" className="-mx-4 mt-3 flex gap-1 overflow-x-auto border-b border-line px-4">
        {convs.length === 0 && <p data-testid="convocation-empty" className="pb-3 text-sm text-dim">Aucune convocation. Crée-en une.</p>}
        {convs.map((c) => (
          <button key={c.id} role="tab" aria-selected={c.id === selectedId} data-testid={`convocation-tab-${c.id}`} onClick={() => onSelect(c.id)} title={c.date}
            className={`-mb-px flex shrink-0 items-center gap-2 rounded-t-xl border px-4 py-2 text-sm font-semibold transition-colors ${c.id === selectedId ? "border-line border-b-pitch bg-ink text-lime" : "border-transparent text-dim hover:text-chalk"}`}>
            {c.active ? <span className="h-2.5 w-2.5 rounded-full bg-lime shadow-[0_0_8px_rgba(212,175,55,0.9)]" aria-label="En ligne" /> : <span className="h-2.5 w-2.5 rounded-full border border-dim" aria-label="Hors ligne" />}
            <span className="max-w-[180px] truncate">{c.adversaire}</span>
          </button>
        ))}
      </div>
      {selected && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span data-testid="convocation-selected-date" className="mr-auto text-sm text-dim">{selected.date} · {selected.competition}</span>
          {selected.active ? (
            <span data-testid="convocation-active-label" title="Affichée aux joueurs" className="inline-flex items-center gap-1.5 rounded-full bg-lime px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-ink">
              <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-ink/60" /><span className="relative inline-flex h-2 w-2 rounded-full bg-ink" /></span>
              En ligne
            </span>
          ) : (
            <span className="inline-flex flex-wrap items-center gap-2">
              <span data-testid="convocation-offline-label" className="inline-flex items-center gap-2 rounded-full border border-dashed border-dim px-3 py-2 text-xs font-bold uppercase tracking-wide text-dim"><EyeOff className="h-4 w-4" />Hors ligne</span>
              <button data-testid="convocation-show-button" onClick={afficher} className="inline-flex items-center gap-1 rounded-full border border-lime px-4 py-2 text-sm font-semibold text-lime transition-colors hover:bg-lime hover:text-ink"><Eye className="h-4 w-4" />Mettre en ligne</button>
            </span>
          )}
          {admin && <ListesWhatsApp key={selected.id} code={code} conv={selected} reponses={reponses} />}
          <a data-testid="convocation-view-public-link" href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border border-line px-4 py-2 text-sm text-chalk transition-colors hover:border-lime hover:text-lime"><ExternalLink className="h-4 w-4" />Voir la page joueurs</a>
          <button data-testid="convocation-close-button" onClick={cloturer} className="inline-flex items-center gap-1 rounded-full border border-lime px-4 py-2 text-sm text-lime transition-colors hover:bg-lime hover:text-ink"><Archive className="h-4 w-4" />Clôturer</button>
          <button data-testid="convocation-delete-button" onClick={supprimer} className="inline-flex items-center gap-1 rounded-full border border-line px-4 py-2 text-sm text-dim hover:border-signal hover:text-signal"><Trash2 className="h-4 w-4" />Supprimer</button>
        </div>
      )}
    </div>
  );
};
